import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, HelpCircle, Link2Off, MessageSquare, SearchCheck, UserSearch } from 'lucide-react';
import Header from '../components/layout/Header';
import MessageForm from '../components/analysis/MessageForm';
import MessageGuideOverlay from '../components/analysis/MessageGuideOverlay';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import { api } from '../services/api';

const features = [
  { icon: UserSearch, title: 'Vrai ou faux expéditeur ?', sub: "On vérifie si c'est vraiment cette personne qui vous écrit" },
  { icon: Link2Off, title: 'Liens piégés', sub: 'On vérifie où mènent vraiment les liens dans le message' },
  { icon: SearchCheck, title: 'Arnaque ou pas ?', sub: 'On repère les techniques classiques des arnaqueurs' },
];

export default function AnalyzeMessagePage() {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showGuide, setShowGuide] = useState(false);
  const navigate = useNavigate();

  const handlePaste = async () => {
    try { setContent(await navigator.clipboard.readText()); setError(''); }
    catch { setError('Impossible de lire le presse-papier. Collez le message manuellement.'); }
  };

  const handleAnalyze = async () => {
    if (!content.trim()) return;
    setLoading(true);
    try {
      const data = await api.analyzeEmail(content.trim());
      api.saveToHistory(data);
      navigate('/results', { state: { type: 'email', data } });
    } catch {
      setLoading(false);
      setError('Analyse impossible pour le moment. Réessayez dans quelques instants.');
    }
  };

  if (loading) return <LoadingSpinner url="Message suspect" currentStep={2} />;

  return (
    <div className="page-container">
      <Header showBack backLabel="Vérifier un message" onBack={() => navigate('/analyze')} />

      {/* Info + bouton guide */}
      <div className="mx-4 mt-4 card border-l-4 border-l-accent-blue">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-accent-blue/10 rounded-xl flex items-center justify-center flex-shrink-0">
            <MessageSquare className="w-6 h-6 text-accent-blue" />
          </div>
          <div className="flex-1">
            <h1 className="font-semibold text-brand-navy text-lg">Collez le message suspect</h1>
            <p className="text-text-muted text-xs mt-1 leading-relaxed">
              Email, SMS, WhatsApp, Messenger… copiez le message que vous avez reçu et collez-le ici. On vérifie tout.
            </p>
            <button
              type="button"
              onClick={() => setShowGuide(true)}
              className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-accent-blue active:opacity-70 transition-opacity"
            >
              <HelpCircle className="w-3.5 h-3.5" />
              Comment faire ?
            </button>
          </div>
        </div>
      </div>

      <div className="mx-4 mt-6"><MessageForm value={content} onChange={setContent} onPaste={handlePaste} /></div>
      {error ? <div className="mx-4 mt-4"><ErrorMessage message={error} /></div> : null}

      <div className="mx-4 mt-6 space-y-3">
        {features.map(({ icon: Icon, title, sub }) => (
          <div key={title} className="h-20 bg-white rounded-xl border border-gray-100 flex items-center gap-4 px-4">
            <span className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center text-brand-navy"><Icon className="w-6 h-6" /></span>
            <div><p className="font-semibold text-brand-navy">{title}</p><p className="text-sm text-text-muted">{sub}</p></div>
          </div>
        ))}
      </div>

      <div className="sticky-action-bar">
        <button type="button" onClick={handleAnalyze} disabled={!content.trim()} className="btn-primary disabled:opacity-40 disabled:cursor-not-allowed">Analyser ce message <ArrowRight className="w-5 h-5" /></button>
        <p className="text-center text-text-subtle text-xs mt-2">Vos données restent confidentielles</p>
      </div>

      {showGuide && <MessageGuideOverlay onClose={() => setShowGuide(false)} />}
    </div>
  );
}
